import {
	getImproveAPI,
	GetTilesResponse,
	GetChartResponse,
	GetCompletedLessonsResponse,
	GetCompletedCoursesResponse,
	GetActiveUsersResponse,
	GetInactiveUsersResponse,
	GetTimeInTrainingResponse,
	GetOverdueLessonsResponse,
	StartExportRequest,
	StartExportResponse,
	TeamProgressControllerGetTilesParams,
	TeamProgressControllerGetChartParams,
	TeamProgressControllerGetCompletedLessonsParams,
	TeamProgressControllerGetCompletedCoursesParams,
	TeamProgressControllerGetActiveUsersParams,
	TeamProgressControllerGetInactiveUsersParams,
	TeamProgressControllerGetTimeInTrainingParams,
	TeamProgressControllerGetOverdueLessonsParams,
	CompletedLessonDto,
	CompletedCourseDto,
	ActiveUserDto,
	InactiveUserDto,
	TimeInTrainingDto,
	OverdueLessonsDto
} from "../gen";
import {useImproveAxios} from "../../hooks/useImproveAxios";
import {useQuery, useMutation} from "@tanstack/react-query";

// Re-export generated DTO types for convenience (consumers can import from this repo or from gen)
export type {CompletedLessonDto, CompletedCourseDto, ActiveUserDto, InactiveUserDto, TimeInTrainingDto, OverdueLessonsDto};

/**
 * Stale time configuration for Team Progress queries
 * 
 * - Tiles & Chart: 2 minutes (less frequently changing aggregate data)
 * - Data grids: 1 minute (more detailed data, may change more frequently)
 * 
 * Note: React Query will still refetch when query keys change (e.g., filter changes),
 * but will use cached data within staleTime window for identical requests.
 */
const STALE_TIME = {
	TILES: 2 * 60 * 1000, // 2 minutes
	CHART: 2 * 60 * 1000, // 2 minutes
	GRIDS: 1 * 60 * 1000, // 1 minute
} as const;

/**
 * Query keys for Team Progress endpoints
 */
export const TEAM_PROGRESS_QUERY_KEYS = {
	TILES: "team-progress-tiles",
	CHART: "team-progress-chart",
	COMPLETED_LESSONS: "team-progress-completed-lessons",
	COMPLETED_COURSES: "team-progress-completed-courses",
	ACTIVE_USERS: "team-progress-active-users",
	INACTIVE_USERS: "team-progress-inactive-users",
	TIME_IN_TRAINING: "team-progress-time-in-training",
	OVERDUE_LESSONS: "team-progress-overdue-lessons",
	EXPORT: "team-progress-export"
} as const;

/**
 * Get dashboard KPI tiles
 * 
 * Returns 6 metrics: completed lessons, completed courses, time in training,
 * active users, inactive users, and users with overdue lessons.
 */
export const useGetTeamProgressTiles = (
	params: TeamProgressControllerGetTilesParams,
) => {
	const axiosConfig = useImproveAxios();
	const {teamProgressControllerGetTiles} = getImproveAPI();

	return useQuery<GetTilesResponse>({
		queryKey: [
			TEAM_PROGRESS_QUERY_KEYS.TILES, 
			params.dateFrom,
			params.dateTo,
			params.archivedUsers
		],
		queryFn: async () => {
			const response = await teamProgressControllerGetTiles(params, axiosConfig);
			return response.data;
		},
		staleTime: STALE_TIME.TILES
	});
};

/**
 * Get lessons completed chart data
 * 
 * Returns time-series data for lessons completed over the date range.
 * Automatically switches between daily and weekly aggregation based on range.
 */
export const useGetTeamProgressChart = (
	params: TeamProgressControllerGetChartParams,
) => {
	const axiosConfig = useImproveAxios();
	const {teamProgressControllerGetChart} = getImproveAPI();

	return useQuery<GetChartResponse>({
		queryKey: [
			TEAM_PROGRESS_QUERY_KEYS.CHART,
			params.dateFrom,
			params.dateTo,
			params.archivedUsers
		],
		queryFn: async () => {
			const response = await teamProgressControllerGetChart(params, axiosConfig);
			return response.data;
		},
		staleTime: STALE_TIME.CHART,
		structuralSharing: false
	});
};

/**
 * Get paginated completed lessons
 * 
 * Returns a paginated list of completed lessons with filtering, sorting, and search.
 */
export const useGetCompletedLessons = (
	params: TeamProgressControllerGetCompletedLessonsParams,
) => {
	const axiosConfig = useImproveAxios();
	const {teamProgressControllerGetCompletedLessons} = getImproveAPI();

	return useQuery<GetCompletedLessonsResponse>({
		queryKey: [TEAM_PROGRESS_QUERY_KEYS.COMPLETED_LESSONS, params],
		queryFn: async () => {
			const response = await teamProgressControllerGetCompletedLessons(
				params,
				axiosConfig,
			);
			return response.data;
		},
		staleTime: STALE_TIME.GRIDS
	});
};

/**
 * Get paginated completed courses
 * 
 * Returns a paginated list of completed courses with filtering, sorting, and search.
 */
export const useGetCompletedCourses = (
	params: TeamProgressControllerGetCompletedCoursesParams,
) => {
	const axiosConfig = useImproveAxios();
	const {teamProgressControllerGetCompletedCourses} = getImproveAPI();

	return useQuery<GetCompletedCoursesResponse>({
		queryKey: [TEAM_PROGRESS_QUERY_KEYS.COMPLETED_COURSES, params],
		queryFn: async () => {
			const response = await teamProgressControllerGetCompletedCourses(
				params,
				axiosConfig,
			);
			return response.data;
		},
		staleTime: STALE_TIME.GRIDS
	});
};

/**
 * Get paginated active users
 * 
 * Returns a paginated list of active users (users who completed lessons in the date range).
 */
export const useGetActiveUsers = (
	params: TeamProgressControllerGetActiveUsersParams,
) => {
	const axiosConfig = useImproveAxios();
	const {teamProgressControllerGetActiveUsers} = getImproveAPI();

	return useQuery<GetActiveUsersResponse>({
		queryKey: [TEAM_PROGRESS_QUERY_KEYS.ACTIVE_USERS, params],
		queryFn: async () => {
			const response = await teamProgressControllerGetActiveUsers(
				params,
				axiosConfig,
			);
			return response.data;
		},
		staleTime: STALE_TIME.GRIDS
	});
};

/**
 * Get paginated inactive users
 * 
 * Returns a paginated list of inactive users (users who did not complete lessons in the date range).
 */
export const useGetInactiveUsers = (
	params: TeamProgressControllerGetInactiveUsersParams,
) => {
	const axiosConfig = useImproveAxios();
	const {teamProgressControllerGetInactiveUsers} = getImproveAPI();

	return useQuery<GetInactiveUsersResponse>({
		queryKey: [TEAM_PROGRESS_QUERY_KEYS.INACTIVE_USERS, params],
		queryFn: async () => {
			const response = await teamProgressControllerGetInactiveUsers(
				params,
				axiosConfig,
			);
			return response.data;
		},
		staleTime: STALE_TIME.GRIDS
	});
};

/**
 * Get paginated time in training data
 * 
 * Returns a paginated list of users with their total watch time and completed lessons.
 */
export const useGetTimeInTraining = (
	params: TeamProgressControllerGetTimeInTrainingParams,
) => {
	const axiosConfig = useImproveAxios();
	const {teamProgressControllerGetTimeInTraining} = getImproveAPI();

	return useQuery<GetTimeInTrainingResponse>({
		queryKey: [TEAM_PROGRESS_QUERY_KEYS.TIME_IN_TRAINING, params],
		queryFn: async () => {
			const response = await teamProgressControllerGetTimeInTraining(
				params,
				axiosConfig,
			);
			return response.data;
		},
		staleTime: STALE_TIME.GRIDS
	});
};

/**
 * Get paginated users with overdue lessons
 * 
 * Returns a paginated list of users who have overdue lessons.
 */
export const useGetOverdueLessonsForTeamProgress = (
	params: TeamProgressControllerGetOverdueLessonsParams,
) => {
	const axiosConfig = useImproveAxios();
	const {teamProgressControllerGetOverdueLessons} = getImproveAPI();

	return useQuery<GetOverdueLessonsResponse>({
		queryKey: [TEAM_PROGRESS_QUERY_KEYS.OVERDUE_LESSONS, params],
		queryFn: async () => {
			const response = await teamProgressControllerGetOverdueLessons(
				params,
				axiosConfig,
			);
			return response.data;
		},
		staleTime: STALE_TIME.GRIDS
	});
};

/**
 * Start a Team Progress export job
 * 
 * Queues a background job to generate a CSV export of the specified report type.
 * Returns immediately with a job ID. WebSocket event will be emitted on completion.
 */
export const useStartTeamProgressExport = () => {
	const axiosConfig = useImproveAxios();
	const {teamProgressControllerStartExport} = getImproveAPI();

	return useMutation<StartExportResponse, Error, StartExportRequest>({
		mutationKey: [TEAM_PROGRESS_QUERY_KEYS.EXPORT],
		mutationFn: async (data: StartExportRequest) => {
			const response = await teamProgressControllerStartExport(data, axiosConfig);
			return response.data;
		},
	});
};

/**
 * Download CSV from blob storage
 * 
 * Fetches a CSV file from Azure Blob Storage and returns it as a Blob.
 * Used after export job completes and WebSocket event is received.
 * 
 * Note: Using mutation (not query) because:
 * - Triggered manually on WebSocket event (not automatic)
 * - Need onSuccess/onError callbacks for file download
 * - Don't want to cache blob data
 * - One-time operation per export job
 * 
 * Note: Generated types return void for binary responses - casting through unknown
 */
export const useDownloadCsvFromBlob = () => {
	const axiosConfig = useImproveAxios();
	const {blobControllerGetBlob} = getImproveAPI();

	return useMutation<Blob, Error, {containerName: string; blobName: string; mimeType: string}>({
		mutationFn: async ({containerName, blobName, mimeType}) => {
			const response = await blobControllerGetBlob(
				{containerName, blobName, mimeType},
				{...axiosConfig, responseType: 'blob'}
			);
			return response.data as unknown as Blob;
		},
	});
};

